import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import { useGetSftpPartnerFileDownloadUrlMutation } from "store/queries/sftpPartners";
import { ISftpPartnerFile, SftpPartnerFileStatus } from "types/SftpPartner";

import {
  sftpApiErrorMessage,
  startBrowserDownload,
} from "../../utils/sftpPartners";

const STATUS_DISPLAY: Record<
  SftpPartnerFileStatus,
  { label: string; severity: "success" | "info" | "warning" | "danger" | null }
> = {
  completed: { label: "Completed", severity: "success" },
  processing: { label: "Processing", severity: "info" },
  held: { label: "Held", severity: "warning" },
  failed: { label: "Failed", severity: "danger" },
  conversion_deleted: { label: "Conversion deleted", severity: "warning" },
  no_conversion: { label: "No conversion", severity: null },
  succeeded: { label: "Succeeded", severity: "success" },
};

export const sftpFileStatusLabel = (status: SftpPartnerFileStatus) =>
  STATUS_DISPLAY[status]?.label ?? status;

// CLE-2958 — one row's status. Inbound: the folded conversion's live state; outbound: the write's outcome,
// with the system error on hover when it failed.
export function SftpFileStatusTag({ file }: { file: ISftpPartnerFile }) {
  const display = STATUS_DISPLAY[file.status] ?? {
    label: file.status,
    severity: null,
  };
  if (!display.severity)
    return (
      <span className="text-600" data-testid="sftp-file-status">
        {display.label}
      </span>
    );
  return (
    <Tag
      value={display.label}
      severity={display.severity}
      title={file.error ?? undefined}
      data-testid="sftp-file-status"
    />
  );
}

/**
 * CLE-2958 — download one of the partner's files: the server presigns a five-minute S3 GET for a key under
 * the partner's prefix and the browser opens it. The file never passes through the API.
 */
export function useSftpFileDownload(slug: string) {
  const toastRef = useContext(ToastContext);
  const [getUrl] = useGetSftpPartnerFileDownloadUrlMutation();
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const download = async (key: string) => {
    setPendingKey(key);
    try {
      const result = await getUrl({ slug, key }).unwrap();
      // Same tab, not window.open: the user gesture has expired after the await, so a popup would be
      // blocked; the URL is served as an attachment, so the page stays where it is.
      startBrowserDownload(result.data.url);
    } catch (error) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't download the file",
        detail: sftpApiErrorMessage(error, "Please try again."),
        life: 8000,
      });
    } finally {
      setPendingKey(null);
    }
  };

  return { download, pendingKey };
}

export function SftpDownloadButton({
  s3Key,
  onDownload,
  loading,
}: {
  s3Key: string;
  onDownload: (key: string) => void;
  loading: boolean;
}) {
  return (
    <Button
      icon="pi pi-download"
      className="p-button-text p-button-sm"
      tooltip="Download"
      tooltipOptions={{ position: "left" }}
      aria-label={`Download ${s3Key}`}
      loading={loading}
      onClick={() => onDownload(s3Key)}
      data-testid="sftp-file-download"
    />
  );
}
